import { Button } from '../buttons'
import { Callout } from '../callout'
import { GeneralErrorBoundary } from '../common/errorBoundary'
import { ArrowRightIcon, SearchIcon } from '../icons'
import { InputField, RadioInput, Toggle } from '../inputs'
import { JsonViewer } from '../timeline/jsonViewer'
import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import { useState } from 'react'
import TypographyHeader from '../typography/TypographyHeading'
import { Modal } from '../modalsAndOverlays'
import ContentDisclosure from '../common/contentDisclosure'
import { MetadataEntry } from '../timeline'
import { chainIDMapping, formatMetadataType } from '#app/utils/misc.tsx'
import { MetadataProperties } from '#types/loreTypes.tsx'

export default function AIScraper({}: {}) {
	const [filterParams, setFilterParams] = useState<{
		mode: 'searchCriteria' | 'link'
		searchCriteria?: string
		sites?: string
		disableSitesRestriction?: boolean
		supportedLinks?: string
		disableSupportedLinks?: boolean
		offset?: number
		limit?: number
		searchQuery?: string
		returnUrls?: boolean
		url?: string
		targetProfileUrl?: string
		extractInformationPrompt?: string
		proposeAttestationsPrompt?: string
	}>({
		mode: 'searchCriteria',
		limit: 5,
		disableSitesRestriction: true,
		disableSupportedLinks: true,
	})
	const [aiScraperData, setAiScraperData] = useState<any[] | null>(null)
	const [errorMessage, setErrorMessage] = useState('')
	const [loading, setLoading] = useState(false)
	const [helpModalOpen, setHelpModalOpen] = useState(false)
	const [viewResultsMode, setViewResultsMode] = useState('Timeline')

	const handleDataChange = (name: string, value: any) => {
		let newData = { ...filterParams }
		//@ts-ignore
		newData[name] = value
		// If switching modes, clean the form
		if (name === 'mode') {
			if (value === 'searchCriteria') {
				newData = {
					mode: 'searchCriteria',
					limit: 5,
					disableSitesRestriction: true,
					disableSupportedLinks: true,
				}
			}
			if (value === 'link') {
				newData = {
					mode: 'link',
				}
			}
		}
		setFilterParams(newData)
	}

	const handleSubmit = async () => {
		setLoading(true)
		setAiScraperData(null)
		setErrorMessage('')
		try {
			const options = {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					...filterParams,
					...(filterParams.sites !== undefined && {
						sites: filterParams.sites.split(','),
					}),
					...(filterParams.supportedLinks !== undefined && {
						supportedLinks: filterParams.supportedLinks.split(','),
					}),
					...(filterParams.offset !== undefined && {
						offset: Number(filterParams.offset),
					}),
					...(filterParams.limit !== undefined && {
						limit: Number(filterParams.limit),
					}),
					...(filterParams.disableSitesRestriction === true && { sites: [] }),
					...(filterParams.disableSupportedLinks === true && {
						supportedLinks: [],
					}),
				}).replace(' ', ''),
			}

			const aiScraperResponse = await fetch(
				`${import.meta.env.VITE_AI_SCRAPPER_ENDPOINT}`,
				options,
			)
			const jsonResponse = await aiScraperResponse.json()

			if (jsonResponse && !jsonResponse.message) {
				// If the results are URLs instead of attestations, switch view mode to JSON
				if (filterParams.returnUrls) setViewResultsMode('JSON')
				setAiScraperData(jsonResponse)
				setLoading(false)
			} else {
				setErrorMessage(
					`An error has occurred when attempting to run the AI scraper API: ${jsonResponse.message}`,
				)
				setLoading(false)
			}
		} catch (e) {
			console.error(e)
			setErrorMessage(
				'An unexpected error has occurred when attempting to run the AI scraper API.',
			)
			setLoading(false)
		}
	}

	const getNextPage = async () => {
		setLoading(true)
		setErrorMessage('')
		const newLimit =
			filterParams.limit !== undefined ? Number(filterParams.limit) : 10
		const newOffset =
			filterParams.offset !== undefined
				? Number(filterParams.offset) + newLimit
				: newLimit
		try {
			const options = {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					...filterParams,
					...(filterParams.sites !== undefined && {
						sites: filterParams.sites.split(','),
					}),
					...(filterParams.supportedLinks !== undefined && {
						supportedLinks: filterParams.supportedLinks.split(','),
					}),
					offset: Number(newOffset),
					limit: Number(newLimit),
					...(filterParams.disableSitesRestriction === true && { sites: [] }),
					...(filterParams.disableSupportedLinks === true && {
						supportedLinks: [],
					}),
				}).replace(' ', ''),
			}

			const aiScraperResponse = await fetch(
				`${import.meta.env.VITE_AI_SCRAPPER_ENDPOINT}`,
				options,
			)
			const jsonResponse = await aiScraperResponse.json()

			if (jsonResponse && !jsonResponse.message) {
				aiScraperData && aiScraperData.length
					? setAiScraperData([...aiScraperData, ...jsonResponse])
					: setAiScraperData(jsonResponse)
				handleDataChange('offset', newOffset)
				setLoading(false)
			} else {
				setErrorMessage(
					`An error has occurred when attempting to run the AI scraper API: ${jsonResponse.message}`,
				)
				setLoading(false)
			}
		} catch (e) {
			console.error(e)
			setErrorMessage(
				'An unexpected error has occurred when attempting to run the AI scraper API.',
			)
			setLoading(false)
		}
	}

	const buildTimeline = () => {
		let timeline: {
			attestation: {
				createdAt: string
				references: {
					entry?: {
						chain: {
							name: string
						}
						nft: {
							contractAddress?: string
							tokenId?: string
							tokenAddress?: string
						}
					}
					metadataIPFSCID: string
					metadataArweaveTxId: string
				}
				signature: string
				properties: object
				address: string
				message: string
				template: {
					name: string
					slug: string
				}
				uid: string
				userId?: string
				userAvatar?: string
				username?: string
				nftName?: string
			}
			urlToSign: string
		}[] = []
		aiScraperData?.forEach(result => timeline.push(...getPreviews(result)))
		return timeline.map(timelineEntry => (
			<div className="my-2 flex flex-col items-center gap-y-4 rounded-md border border-textInactiveLight p-4 dark:border-textInactiveDark">
				<MetadataEntry metadata={timelineEntry.attestation} />
				{timelineEntry.urlToSign && (
					<a href={timelineEntry.urlToSign} target="_blank">
						<Button variant="primary">Sign Attestation</Button>
					</a>
				)}
			</div>
		))
	}

	const getPreviews = (result: {
		nft: {
			chain: string
			contractAddress: string
			tokenId: string
		}
		attestations: {
			type: string
			properties: MetadataProperties
			urlToSign: string
		}[]
	}) => {
		if (!result.attestations) return []
		return result.attestations.map(attestation => {
			return {
				attestation: {
					createdAt: new Date().toISOString(),
					address: '0x123456789',
					template: {
						name: formatMetadataType(attestation.type),
						slug: attestation.type,
						version: '1.0',
					},
					...(result.nft && {
						references: {
							entry: {
								chain: {
									name: result.nft.chain,
									id: result.nft.chain
										? chainIDMapping[result.nft.chain]
										: 'ETH',
								},
								nft: {
									contractAddress: result.nft.contractAddress,
									tokenId: result.nft.tokenId,
								},
								type: 'Attestation',
							},
						},
					}),
					properties: {
						...attestation.properties,
					},
				},
				urlToSign: attestation.urlToSign,
			}
		})
	}

	return (
		<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-28">
			<div className="flex flex-col gap-y-4">
				<TypographyHeader
					size="large"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					AI Scraper
				</TypographyHeader>
				<div className="flex flex-col">
					<div className="flex flex-col items-start sm:flex-row sm:items-center">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Generate attestations based on a{' '}
						</TypographyParagraph>
						<div className="flex flex-row items-center">
							<RadioInput
								key="Search criteria"
								name="Search criteria"
								size="large"
								onClick={() => handleDataChange('mode', 'searchCriteria')}
								current={filterParams.mode == 'searchCriteria'}
							/>
							<RadioInput
								key="Link"
								name="Link"
								size="large"
								onClick={() => handleDataChange('mode', 'link')}
								current={filterParams.mode == 'link'}
							/>
						</div>
					</div>
					<div className="flex flex-row items-center">
						<TypographyLabel
							size="small"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Need help choosing?
						</TypographyLabel>
						<Button
							variant="tertiary"
							additionalClasses="ml-1 text-textPrimaryLight dark:text-textPrimaryDark"
							onClick={() => setHelpModalOpen(true)}
						>
							Learn how each option works
						</Button>
					</div>
				</div>
				{filterParams.mode == 'searchCriteria' && (
					<div className="flex flex-col gap-y-4">
						<InputField
							name="search criteria"
							type="text"
							placeholder='Main subject of the search. Ex. "Sam Spratt"'
							containerWidth="w-full"
							value={filterParams.searchCriteria}
							onChange={e => {
								handleDataChange('searchCriteria', e.target.value)
							}}
						/>
						<ContentDisclosure
							button={
								<TypographyCaps additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark p-4">
									Show advanced options
								</TypographyCaps>
							}
							content={
								<div className="flex flex-col gap-y-2">
									<InputField
										name="sites"
										type="text"
										placeholder='Site domains as comma separated values. Ex. "twitter.com, instagram.com"'
										containerWidth="w-full"
										isInactive={filterParams.disableSitesRestriction}
										value={filterParams.sites}
										onChange={e => {
											handleDataChange(
												'sites',
												e.target.value.replaceAll(' ', ''),
											)
										}}
									/>
									<div className="mt-2 flex flex-row">
										<Toggle
											name={''}
											isEnabled={
												filterParams.disableSitesRestriction
													? filterParams.disableSitesRestriction
													: false
											}
											onEnabledChange={newEnabled =>
												handleDataChange('disableSitesRestriction', newEnabled)
											}
										></Toggle>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											<b>Disable sites restriction: </b>This will lift the
											default site restrictions for the results. While this will
											provide a wider range of results, it can also include
											non-relevant sites. It is meant only in cases in which
											restricting the sites yields no information.
										</TypographyParagraph>
									</div>
									<InputField
										name="supported links"
										type="text"
										placeholder='Site domains to be included within the search results contents (visibly) as comma separated values. Ex. "etherscan.io, solana.fm"'
										containerWidth="w-full"
										isInactive={filterParams.disableSupportedLinks}
										value={filterParams.supportedLinks}
										onChange={e => {
											handleDataChange(
												'supportedLinks',
												e.target.value.replaceAll(' ', ''),
											)
										}}
									/>
									<div className="mt-2 flex flex-row">
										<Toggle
											name={''}
											isEnabled={
												filterParams.disableSupportedLinks
													? filterParams.disableSupportedLinks
													: false
											}
											onEnabledChange={newEnabled =>
												handleDataChange('disableSupportedLinks', newEnabled)
											}
										></Toggle>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											<b>Disable supported links: </b>This will lift the default
											restrictions that demand results to visibly include at
											least one refenrece to a link supported by Lore's search
											functionality. While this will provide a wider range of
											results, it can also include sites with no valid links. It
											is meant only in cases in which restricting the sites
											yields no information.
										</TypographyParagraph>
									</div>
									<InputField
										name="limit"
										type="number"
										placeholder="Max amount of results fetched. Default is 100. Ex. 200"
										containerWidth="w-full"
										value={filterParams.limit}
										onChange={e => {
											handleDataChange('limit', e.target.value)
										}}
									/>
									<InputField
										name="offset"
										type="number"
										placeholder="Skip the first N results fetched. Default is 0. Ex. 10"
										containerWidth="w-full"
										value={filterParams.offset}
										onChange={e => {
											handleDataChange('offset', e.target.value)
										}}
									/>
									<InputField
										name="search query"
										type="text"
										placeholder='Full search query to override the system generated query. Ex. "Sam Spratt nft art AND ( (site:twitter.com inurl:status) OR  (site:x.com inurl:status))"'
										containerWidth="w-full"
										value={filterParams.searchQuery}
										onChange={e => {
											handleDataChange('searchQuery', e.target.value)
										}}
									/>
									<div className="mt-2 flex flex-row">
										<Toggle
											name={''}
											isEnabled={
												filterParams.returnUrls
													? filterParams.returnUrls
													: false
											}
											onEnabledChange={newEnabled =>
												handleDataChange('returnUrls', newEnabled)
											}
										></Toggle>
										<TypographyParagraph
											size="medium"
											additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										>
											<b>Return URLs: </b>Enable to make the API return just the
											URLs fetched from Google, instead of generating
											attestations. This is useful to test the combination of
											the provided filters to get relevant URLs before
											attempting to generate attestations (which consume OpenAI
											credits).
										</TypographyParagraph>
									</div>
								</div>
							}
						/>
					</div>
				)}
				{filterParams.mode == 'link' && (
					<div className="flex flex-col gap-y-4">
						<InputField
							name="url"
							type="text"
							placeholder='URL to scrappe to generate attestations Ex. "https://twitter.com/osf_rekt/status/1764712134888325534"'
							containerWidth="w-full"
							value={filterParams.url}
							onChange={e => {
								handleDataChange('url', e.target.value)
							}}
						/>
						<InputField
							name="NFT/Collection profile URL (optional)"
							type="text"
							placeholder='NFT or Collection profile URL to sign the generated attestations. Ex. "https://atomiclore.io/nftProfile/ethereum/0x5755ab845ddeab27e1cfce00cd629b2e135acc3d/1610"'
							containerWidth="w-full"
							value={filterParams.targetProfileUrl}
							onChange={e => {
								handleDataChange('targetProfileUrl', e.target.value)
							}}
						/>
					</div>
				)}
				<Button
					variant="primary"
					leftIcon={<SearchIcon />}
					onClick={() => handleSubmit()}
					disabled={loading}
				>
					{loading ? 'Retrieving results...' : 'Generate attestations'}
				</Button>
				{aiScraperData && aiScraperData.length !== 0 && (
					<div className="flex flex-col gap-y-4">
						<Callout
							variant="success"
							content={`${aiScraperData.length} result(s) found!`}
							additionalClasses="w-full mb-4"
						/>
						<div className="flex flex-col items-start sm:flex-row sm:items-center">
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								View results as{' '}
							</TypographyParagraph>
							<div className="flex flex-row items-center">
								<RadioInput
									key="Timeline"
									name="Timeline"
									size="large"
									onClick={() => setViewResultsMode('Timeline')}
									current={viewResultsMode == 'Timeline'}
								/>
								<RadioInput
									key="JSON"
									name="JSON"
									size="large"
									onClick={() => setViewResultsMode('JSON')}
									current={viewResultsMode == 'JSON'}
								/>
							</div>
						</div>
						{viewResultsMode == 'Timeline' && buildTimeline()}
						{viewResultsMode == 'JSON' && (
							<JsonViewer src={aiScraperData} theme={'dark'} />
						)}
					</div>
				)}
				{!loading &&
					filterParams.mode == 'searchCriteria' &&
					aiScraperData &&
					aiScraperData.length !== 0 && (
						<div className="flex flex-col items-center gap-y-4">
							<TypographyParagraph
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Didn't find anything worth signing?
							</TypographyParagraph>
							<Button
								variant="secondary"
								rightIcon={<ArrowRightIcon />}
								onClick={() => getNextPage()}
								disabled={loading}
							>
								{`Check out the next ${filterParams.limit} search result(s)`}
							</Button>
						</div>
					)}
				{aiScraperData && !aiScraperData.length && (
					<div className="flex flex-col gap-y-4">
						<Callout
							variant="warning"
							content="No results found. Please provide a different link or search criteria."
							additionalClasses="w-full mb-4"
						/>
					</div>
				)}
				{errorMessage && errorMessage.length && (
					<Callout
						variant="error"
						content={errorMessage}
						additionalClasses="w-full mb-4"
					/>
				)}
			</div>
			<Modal
				size=""
				body={
					<div className="flex flex-col gap-y-2">
						<TypographyHeader
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Generating attestations
						</TypographyHeader>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							This scraper allows users to search and generate attestations
							using two methods:
						</TypographyParagraph>
						<TypographyParagraph
							size="large"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Search criteria
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							If a search criteria is provided:
							<br />
							1. The system will fetch results from Google based on that
							criteria, alongside several additional optional parameters. Plus,
							all results will be forced to include at least one of the
							supported domains from Lore's search functionality. This allows
							the system to detect NFT links in the result's body and generate
							attestations related to those NFTs.
							<br />
							2. Once the results have been fetched, their content will be
							processed to detect potential NFT links.
							<br />
							3. If at least one NFT is detected, the whole content will be
							passed to an OpenAI AI model to map elements from the content to a
							structured JSON with fields that can be used for attestations.
							<br />
							4. Once the content has been processed by the AI model, we verify
							if we have all fields that are required to generate an
							attestation. All possible attestations are generated and returned
							as an object in the response.
							<br />
							5. The generated attestations also include a link to Atomic Lore
							to open the NFT profile page, and the sign form, with the template
							selected and all the information populated
						</TypographyParagraph>
						<TypographyParagraph
							size="large"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Link
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							If a single link is provided, it will perform steps 2 through 4
							from the search criteria flow as if it was an URL obtained from
							SerpAPI.
							<br />
							This doesn't retrieve results from Google, thus it doesn't have a
							usage limit other than the OpenAI credits.
							<br />
							For a single URL, found attestations will be returned, even if no
							NFT is found in the provided content, so that the user can then
							provide the NFT information on their end.
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							For more information about optional parameters and configurations,
							see{' '}
							<a
								href="https://github.com/atomicform/ai-scrapper"
								target="_blank"
							>
								https://github.com/atomicform/ai-scrapper
							</a>
						</TypographyParagraph>
					</div>
				}
				isOpen={helpModalOpen}
				onClose={() => setHelpModalOpen(false)}
			/>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
